import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface ContactPanelConfig {
  showEmail: boolean;
  showPhone: boolean;
  showMobile: boolean;
  showCompany: boolean;
  showJobTitle: boolean;
  showLocation: boolean;
  showLastConversation: boolean;
  showContactAdded: boolean;
  showCustomAttributes: boolean;
  showNotes: boolean;
  showTags: boolean;
}

const DEFAULT_CONFIG: ContactPanelConfig = {
  showEmail: true,
  showPhone: true,
  showMobile: true,
  showCompany: true,
  showJobTitle: true,
  showLocation: true,
  showLastConversation: true,
  showContactAdded: true,
  showCustomAttributes: true,
  showNotes: true,
  showTags: true,
};

interface FieldDef {
  key: keyof ContactPanelConfig;
  label: string;
  description: string;
}

const FIELD_GROUPS: { title: string; fields: FieldDef[] }[] = [
  {
    title: "Contact Info",
    fields: [
      { key: "showEmail", label: "Email", description: "Contact's email address" },
      { key: "showPhone", label: "Phone", description: "Primary phone number" },
      { key: "showMobile", label: "Mobile", description: "Mobile / cell phone number" },
      { key: "showCompany", label: "Company", description: "Company or organization name" },
      { key: "showJobTitle", label: "Job Title", description: "Contact's role or title" },
      { key: "showLocation", label: "Location", description: "Address, city, state, country" },
    ],
  },
  {
    title: "Contact Metadata",
    fields: [
      { key: "showLastConversation", label: "Last Conversation", description: "Date of most recent conversation" },
      { key: "showContactAdded", label: "Contact Added", description: "Date the contact was created" },
    ],
  },
  {
    title: "Sections",
    fields: [
      { key: "showCustomAttributes", label: "Custom Fields", description: "Custom contact attributes configured by your team" },
      { key: "showNotes", label: "Notes", description: "Agent notes attached to the contact" },
      { key: "showTags", label: "Tags", description: "Tags applied to the contact" },
    ],
  },
];

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={`Toggle ${label}`}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cornflower-blue-500 ${
        checked ? "bg-cornflower-blue-500" : "bg-slate-gray-300"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

export default function ContactPanelSettingsPanel() {
  const api = useApi();
  const { displayToast } = useToastAlert();

  const [config, setConfig] = useState<ContactPanelConfig>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await api.messaging.get("admin/contact-panel-config");
      if (resp.ok) {
        const data = await resp.json();
        setConfig({ ...DEFAULT_CONFIG, ...data });
      }
    } catch {
      // safe default — all fields shown
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  function setField(key: keyof ContactPanelConfig, value: boolean) {
    setConfig((c) => ({ ...c, [key]: value }));
    setDirty(true);
  }

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const resp = await api.messaging.put("admin/contact-panel-config", config);
      if (resp.ok) {
        displayToast(Severity.Success, "Contact panel settings saved.");
        setDirty(false);
      } else {
        displayToast(Severity.Error, "Failed to save contact panel settings.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to save contact panel settings.");
    } finally {
      setSaving(false);
    }
  }, [api.messaging, config, displayToast]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-slate-400 text-sm">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-gray-800">Contact Panel</h2>
        <p className="text-sm text-slate-gray-500 mt-1">
          Control which fields agents see in the contact sidebar when viewing a conversation.
          Changes apply to all agents site-wide.
        </p>
      </div>

      {/* Required fields notice */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-gray-800">Name</h3>
              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full font-medium">
                Required
              </span>
            </div>
            <p className="text-sm text-slate-gray-500 mt-0.5">
              Contact's name — always shown
            </p>
          </div>
          <div className="w-11 h-6 rounded-full bg-cornflower-blue-500 opacity-40 cursor-not-allowed" aria-disabled="true" />
        </div>
      </div>

      {/* Field groups */}
      {FIELD_GROUPS.map((group) => (
        <div key={group.title} className="bg-white rounded-lg border border-slate-gray-200 divide-y divide-slate-100">
          <div className="px-5 py-3">
            <h3 className="font-semibold text-slate-gray-700 text-sm uppercase tracking-wide">
              {group.title}
            </h3>
          </div>
          {group.fields.map((field) => (
            <div key={field.key} className="flex items-center justify-between px-5 py-4">
              <div>
                <div className="font-medium text-slate-gray-800 text-sm">{field.label}</div>
                <div className="text-xs text-slate-gray-500 mt-0.5">{field.description}</div>
              </div>
              <Toggle
                checked={config[field.key]}
                onChange={(v) => setField(field.key, v)}
                label={field.label}
              />
            </div>
          ))}
        </div>
      ))}

      {/* Info box */}
      <div className="bg-cornflower-blue-50 border border-cornflower-blue-200 rounded-lg p-5">
        <h3 className="font-semibold text-cornflower-blue-800 mb-2">How it works</h3>
        <ul className="text-sm text-cornflower-blue-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">·</span>
            <span>Hidden fields are still stored — agents just won't see them in the sidebar. Data is never deleted.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">·</span>
            <span>New fields added to the system will appear here automatically and default to visible.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cornflower-blue-500 font-bold mt-0.5">·</span>
            <span>Changes take effect immediately for all agents on next page load.</span>
          </li>
        </ul>
      </div>

      {/* Save button */}
      {dirty && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-cornflower-blue-500 text-white rounded-lg text-sm font-medium hover:bg-cornflower-blue-600 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cornflower-blue-500"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}
    </div>
  );
}
